/**
 * Base Text Input with no styling.
 */

import { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import { textAlign, typography } from 'styled-system'
import { useTranslation } from 'react-i18next'
import { FixMeAny } from 'types/global'
import { MAX_INT_SIZE, MIN_INT_SIZE, MAX_DECIMAL_8, MIN_DECIMAL_8 } from 'utils/db'
import { InputProps, TextAreaProps, BaseTextInputProps } from './types'
import { wholeNumbersRegex, currencyRegexTypeText } from 'utils/regex'
import { addTrailingZerosToCurrency, formatCurrencyNonRegional, formatCurrencyToNumber } from 'utils/formatter'
import Spinner from '../Spinner'

const BaseTextInput = forwardRef<HTMLInputElement, BaseTextInputProps>((props, ref) => {
  const { t } = useTranslation()

  const { value, readOnly, onChange, placeholder, hidePassword, autoComplete = 'off', allowEmptyString = true } = props
  const val = value === null ? '' : value
  const plcHolder = placeholder ? t(placeholder) : undefined

  if (isTextArea(props)) {
    const txtAreaProps: TextAreaProps = {
      ...props,
      value: val,
      placeholder: plcHolder,
      readOnly: readOnly || !onChange,
      autoComplete,
    }
    return (
      <>
        <TextArea aria-label={props.label} {...txtAreaProps} />
        {props.isLoading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
      </>
    )
  }

  const handleChange = (e: FixMeAny) => {
    // Prevent input with only spaces if allowEmptyString is false
    if (!allowEmptyString && /^\s+$/.test(e.target.value)) {
      onChange?.({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          value: '',
        },
      })
      return
    }

    switch (props.format) {
      case 'currency':
        if (!currencyRegexTypeText.test(e.target.value)) return
        break
      case 'whole-positive':
        if (!wholeNumbersRegex.test(e.target.value)) return
        break
      default:
        if (props.type === 'number') {
          if (!wholeNumbersRegex.test(e.target.value)) return
        }
    }

    if (isInput(props) && props.type === 'number') {
      if (!e.target.value) {
        onChange?.(e)
        return
      }

      const num = props.format === 'currency' ? Number(formatCurrencyToNumber(e.target.value)) : Number(e.target.value)

      if (Number.isNaN(num) || e.target.value.endsWith('.')) {
        onChange?.(e)
        return
      }

      const configMax = props.format === 'whole-positive' ? MAX_INT_SIZE : MAX_DECIMAL_8
      const configMin = props.format === 'whole-positive' ? MIN_INT_SIZE : MIN_DECIMAL_8
      const max = props.max !== undefined ? Number(props.max) : configMax
      const min = props.min !== undefined ? Number(props.min) : configMin
      let capped = num
      if (num > max) {
        capped = max
      }
      if (num < min) {
        capped = min
      }

      onChange?.({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          value: `${capped}${addTrailingZerosToCurrency(e.target.value, props.format)}`,
        },
      })
      return
    }

    onChange?.(e)
  }

  const resolvedNumType = props.type === 'number' ? 'text' : props.type
  const toggledType = hidePassword ? 'password' : 'text'
  const resolvePasswordType = props.type === 'password' ? toggledType : resolvedNumType

  const displayValue = props.format === 'currency' ? formatCurrencyNonRegional(String(val)) : val

  const txtProps: InputProps = {
    ...props,
    autoComplete,
    type: resolvePasswordType,
    value: displayValue,
    onChange: handleChange,
    readOnly: readOnly || !onChange,
    placeholder: plcHolder,
  }

  return (
    <>
      <Input ref={ref} aria-label={props.label} {...txtProps} />
      {props.isLoading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
    </>
  )
})

function isTextArea(p: FixMeAny): p is TextAreaProps {
  return (p as TextAreaProps).type === 'textarea'
}

function isInput(p: FixMeAny): p is InputProps {
  return (p as InputProps).type !== 'textarea'
}

const Input = styled.input<InputProps>`
  ${typography}
  ${textAlign}
  ${(props) => props.theme.font({ color: props.color ?? 'defaultText' })}
  height: 100%;
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.48 : 1)};

  &:not(:focus-visible) {
    text-decoration: ${(props) => (props.textDecoration ? props.textDecoration : 'none')};
  }
`

const renderHeight = ({ height }: { height?: string | number }) => css`
  ${height ? (typeof height === 'string' ? height : `${height}px`) : `100%`}
`

const TextArea = styled.textarea<TextAreaProps>`
  ${typography}
  ${textAlign}
  ${(props) => props.theme.font({ color: props.color ?? 'defaultText' })}
  min-height: 5rem;
  height: ${renderHeight};
  ${({ maxHeight }) => maxHeight && `max-height: ${typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`};`}
  width: 100%;
  resize: ${({ resize = 'vertical' }) => resize};
  opacity: ${(props) => (props.disabled ? 0.48 : 1)};
  padding-top: 1rem;
  padding-bottom: 1rem;

  :focus-visible {
    outline: none;
  }

  &:not(:focus-visible) {
    text-decoration: ${(props) => (props.textDecoration ? props.textDecoration : 'none')};
  }
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 1rem;
`

export default BaseTextInput
